/**
 * The files a protocol carries: ones the author attached on step 3, and the
 * attachments of the issues it lists. Pure planning only — which files, in
 * what order, under which heading. Fetching their bytes is
 * `protocolAttachmentLoader`; drawing them is `protocolPdfAttachments`.
 */
import type { LinkedDocument, PMIssue } from "@/domains/pm/types";

/**
 * One attached file. A file from the author's computer stays in the browser:
 * it goes into the PDF, not into the project's documents, so attaching a photo
 * to a protocol never files it into Franklin as a side effect.
 */
export type ProtocolAttachment =
  | { key: string; origin: "local"; file: File; url: string }
  | { key: string; origin: "stored"; doc: LinkedDocument };

/** What the PDF carries for one file, once its bytes are in hand. */
export type ProtocolAttachmentContent =
  | { type: "image"; bytes: Uint8Array; format: "png" | "jpg" }
  | { type: "pdf"; bytes: Uint8Array }
  /** A format the PDF cannot carry (a drawing, a spreadsheet): listed by name. */
  | { type: "unsupported" }
  /** The bytes could not be fetched or read: listed, and said so. */
  | { type: "failed" };

/** A file's own bytes, carried inside the PDF for opening at full quality. */
export interface ProtocolOriginal {
  bytes: Uint8Array;
  mimeType: string | null;
}

export function protocolAttachmentFilename(attachment: ProtocolAttachment): string {
  return attachment.origin === "local" ? attachment.file.name : attachment.doc.filename;
}

/**
 * Identity of the bytes rather than of the attachment: the same Franklin
 * document reached twice — picked on step 3 and attached to an issue, or
 * attached to two issues — is embedded once and listed under each.
 */
export function protocolAttachmentIdentity(attachment: ProtocolAttachment): string {
  return attachment.origin === "stored" ? `doc:${attachment.doc.id}` : attachment.key;
}

/** The key an issue's attachment is planned under — per issue, so a file
 *  shared by two issues is still listed under each. */
export function issueAttachmentKey(issueId: number, documentId: number): string {
  return `issue:${issueId}:doc:${documentId}`;
}

export function storedProtocolAttachment(doc: LinkedDocument, key: string): ProtocolAttachment {
  return { key, origin: "stored", doc };
}

export interface ProtocolAttachmentPlanFile {
  /** 1-based, in print order. What the index and the captions call it. */
  number: number;
  identity: string;
  attachment: ProtocolAttachment;
}

export interface ProtocolAttachmentPlanGroup {
  /** Null for the author's own attachments. */
  issueId: number | null;
  /** `#12 Leaking roof` for an issue; null for the author's own. */
  heading: string | null;
  /** The files listed under this heading. A shared file repeats its number. */
  numbers: number[];
}

/** Where the issues' own attachments print. At least one is always on. */
export interface IssueAttachmentPlacement {
  /** A thumbnail column at the end of the issue table. */
  inTable: boolean;
  /** Full pages in the appendix, under each issue's heading. */
  inAppendix: boolean;
}

export interface ProtocolAttachmentPlan {
  files: ProtocolAttachmentPlanFile[];
  /** What the appendix prints, in order. */
  groups: ProtocolAttachmentPlanGroup[];
  /** Per issue, the files its table row shows. Null when the table shows none. */
  table: Map<number, number[]> | null;
}

interface PlanInput {
  /** The author's own, in the order they were attached. Always the appendix. */
  added: readonly ProtocolAttachment[];
  /** Null when issue attachments are not being included. */
  issueAttachments: ReadonlyMap<number, readonly LinkedDocument[]> | null;
  placement: IssueAttachmentPlacement;
  /** The issues the protocol lists, in the order it lists them. */
  issues: readonly PMIssue[];
}

/**
 * Number every distinct file once: the author's own first — they attached
 * them to THIS protocol — then each issue's, in the issue table's order.
 */
export function planProtocolAttachments({
  added,
  issueAttachments,
  placement,
  issues,
}: PlanInput): ProtocolAttachmentPlan {
  const files: ProtocolAttachmentPlanFile[] = [];
  const numberByIdentity = new Map<string, number>();
  const numberFor = (attachment: ProtocolAttachment): number => {
    const identity = protocolAttachmentIdentity(attachment);
    const known = numberByIdentity.get(identity);
    if (known != null) return known;
    const number = files.length + 1;
    numberByIdentity.set(identity, number);
    files.push({ number, identity, attachment });
    return number;
  };

  const groups: ProtocolAttachmentPlanGroup[] = [];
  if (added.length > 0) {
    groups.push({ issueId: null, heading: null, numbers: dedupe(added.map(numberFor)) });
  }
  const showIssues = issueAttachments && (placement.inTable || placement.inAppendix);
  const table = showIssues && placement.inTable ? new Map<number, number[]>() : null;
  if (showIssues) {
    for (const issue of issues) {
      const documents = issueAttachments.get(issue.id) ?? [];
      if (documents.length === 0) continue;
      const numbers = dedupe(
        documents.map((doc) =>
          numberFor(storedProtocolAttachment(doc, issueAttachmentKey(issue.id, doc.id)))
        )
      );
      table?.set(issue.id, numbers);
      if (placement.inAppendix) {
        groups.push({
          issueId: issue.id,
          heading: `#${issue.issue_number} ${issue.title}`,
          numbers,
        });
      }
    }
  }
  return { files, groups, table };
}

/** A document linked to the same issue twice is listed once under it. */
function dedupe(numbers: number[]): number[] {
  return Array.from(new Set(numbers));
}
